/**
 * Centralized logging service for the application
 * Provides structured logging with different levels and environment-aware output
 */

// Log levels
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

// Current log level based on environment
const CURRENT_LOG_LEVEL = process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;

/**
 * Format log message with timestamp and context
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {string} context - Context/module name
 * @param {any} data - Additional data to log
 * @returns {string} Formatted message
 */
const formatMessage = (level, message, context, data) => {
  const timestamp = new Date().toISOString();
  const contextStr = context ? `[${context}]` : '';
  const baseMessage = `${timestamp} ${level} ${contextStr} ${message}`;
  
  if (data) {
    return `${baseMessage} - Data:`;
  }
  return baseMessage;
};

/**
 * Log error messages
 * @param {string} message - Error message
 * @param {Error|any} error - Error object or additional data
 * @param {string} context - Context/module name
 */
export const logError = (message, error = null, context = null) => {
  if (CURRENT_LOG_LEVEL >= LOG_LEVELS.ERROR) {
    // eslint-disable-next-line no-console
    console.error(formatMessage('ERROR', message, context), error || '');
  }
};

/**
 * Log warning messages
 * @param {string} message - Warning message
 * @param {any} data - Additional data
 * @param {string} context - Context/module name
 */
export const logWarning = (message, data = null, context = null) => {
  if (CURRENT_LOG_LEVEL >= LOG_LEVELS.WARN) {
    // eslint-disable-next-line no-console
    console.warn(formatMessage('WARN', message, context), data || '');
  }
};

/**
 * Log info messages
 * @param {string} message - Info message
 * @param {any} data - Additional data
 * @param {string} context - Context/module name
 */
export const logInfo = (message, data = null, context = null) => {
  if (CURRENT_LOG_LEVEL >= LOG_LEVELS.INFO) {
    // eslint-disable-next-line no-console
    console.info(formatMessage('INFO', message, context), data || '');
  }
};

/**
 * Log debug messages (development only)
 * @param {string} message - Debug message
 * @param {any} data - Additional data
 * @param {string} context - Context/module name
 */
export const logDebug = (message, data = null, context = null) => {
  if (CURRENT_LOG_LEVEL >= LOG_LEVELS.DEBUG) {
    // eslint-disable-next-line no-console
    console.log(formatMessage('DEBUG', message, context), data || '');
  }
};

/**
 * Log API requests for debugging
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {any} data - Request data
 */
export const logApiRequest = (method, url, data = null) => {
  if (CURRENT_LOG_LEVEL >= LOG_LEVELS.DEBUG) {
    logDebug(`API ${method} ${url}`, data, 'API');
  }
};

/**
 * Log API responses for debugging
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {number} status - Response status
 * @param {any} data - Response data
 */
export const logApiResponse = (method, url, status, data = null) => {
  if (CURRENT_LOG_LEVEL >= LOG_LEVELS.DEBUG) {
    logDebug(`API ${method} ${url} - Status: ${status}`, data, 'API');
  }
};

/**
 * Log performance metrics
 * @param {string} operation - Operation name
 * @param {number} duration - Duration in milliseconds
 * @param {any} metadata - Additional metadata
 */
export const logPerformance = (operation, duration, metadata = null) => {
  if (CURRENT_LOG_LEVEL >= LOG_LEVELS.INFO) {
    logInfo(`Performance: ${operation} took ${duration}ms`, metadata, 'PERF');
  }
};

/**
 * Log user actions for analytics
 * @param {string} action - User action
 * @param {any} metadata - Action metadata
 */
export const logUserAction = (action, metadata = null) => {
  if (CURRENT_LOG_LEVEL >= LOG_LEVELS.INFO) {
    logInfo(`User action: ${action}`, metadata, 'USER');
  }
};

// Default export for convenience
const LoggingService = {
  error: logError,
  warning: logWarning,
  info: logInfo,
  debug: logDebug,
  apiRequest: logApiRequest,
  apiResponse: logApiResponse,
  performance: logPerformance,
  userAction: logUserAction,
};

export default LoggingService;